'use client';

import { css, cx } from '@onbeam/business-styled-system/css';
import {
  circle,
  flex,
  text,
  vstack,
} from '@onbeam/business-styled-system/patterns';
import {
  ApplicationsListProps,
  ApplicationStatus,
} from './ApplicationsList.types';
import { useSearchParams } from 'next/navigation';
import { applicationItems } from './ApplicationsList.data';
import { tabButtonStyle } from './ApplicationsList.styles';
import { ApplicationItem } from './ApplicationItem/ApplicationItem';
import { SeedPlantIcon } from '@onbeam/icons/business';

export const ApplicationsList = ({
  className,
  ...props
}: ApplicationsListProps) => {
  const params = useSearchParams();
  const activeTab = params.get('tab');

  const handleTabChange = (tab: string) => {
    const searchParams = new URLSearchParams(params);
    if (tab === '') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    window.history.pushState({}, '', `?${searchParams}`);
  };

  const approvedItems = applicationItems.filter(
    (item) => item.status === ApplicationStatus.APPROVED,
  );
  const deniedItems = applicationItems.filter(
    (item) => item.status === ApplicationStatus.DENIED,
  );
  const activeItems = activeTab === 'denied' ? deniedItems : approvedItems;

  return (
    <section
      className={cx(
        vstack({
          gap: '8',
          align: 'center',
        }),
        className,
      )}
      {...props}
    >
      <div
        className={flex({
          w: 'full',
          gap: '4',
        })}
      >
        <button
          type="button"
          data-active={!activeTab}
          className={tabButtonStyle}
          onClick={() => handleTabChange('')}
        >
          Approved ({approvedItems.length})
        </button>
        <button
          type="button"
          data-active={activeTab === 'denied'}
          className={tabButtonStyle}
          onClick={() => handleTabChange('denied')}
        >
          Denied ({deniedItems.length})
        </button>
      </div>
      {!activeItems.length ? (
        <div
          className={vstack({
            gap: '2.5',
            align: 'center',
            textAlign: 'center',
            py: '10',
            md: {
              py: '8',
            },
          })}
        >
          <div className={circle({ bg: 'mono.senary', p: '3', mb: '1.5' })}>
            <SeedPlantIcon />
          </div>
          <span className={text({ style: 'body-lg' })}>
            {activeTab === 'denied'
              ? 'No rejections to show'
              : 'No approved applications yet'}
          </span>
          <p
            className={text({
              style: 'body-md',
              color: 'mono.tertiary',
              maxW: '[20.5rem]',
            })}
          >
            {activeTab === 'denied'
              ? 'Denied applications are listed here to ensure transparency around the review process.'
              : 'Once your grant is approved, it will appear here with full details and status updates.'}
          </p>
        </div>
      ) : (
        <ul
          className={vstack({
            w: 'full',
            gap: '16',
            md: {
              gap: '6',
            },
          })}
        >
          {activeItems.map((item) => (
            <ApplicationItem key={item.id} {...item} />
          ))}
        </ul>
      )}
      <hr className={css({ color: 'opacity.md', w: 'full' })} />
      <p
        className={text({
          style: 'small-xs',
          fontWeight: 'bold',
          color: 'mono.tertiary',
          align: 'center',
          maxW: '[39rem]',
        })}
      >
        All grants reviewed by the Beam Foundation are listed here for full
        transparency — including approved and rejected proposals. Explore what’s
        already being built (or wasn’t), and see how Beam grants are fuelling
        the ecosystem.
      </p>
    </section>
  );
};
